import CloseIcon from '@mui/icons-material/Close';
import { IconButton, Modal } from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { useModalRegistration } from '@stores/ModalStore';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import css from './DeleteChatModal.module.scss';

export type DeleteChatModalRef = {
  open: () => void;
  close: () => void;
};

interface Props {
  chatTitle: string;
  /** Performs the delete. The modal awaits this promise and closes on
   *  success; throwing keeps the modal open so the user can retry. */
  onConfirm: () => Promise<void>;
}

export const DeleteChatModal = observer(
  forwardRef<DeleteChatModalRef, Props>(({ chatTitle, onConfirm }, ref) => {
    const [open, setOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useModalRegistration('chat-delete', open);

    const onOpen = () => {
      setIsDeleting(false);
      setOpen(true);
    };
    const onClose = () => {
      if (isDeleting) return;
      setOpen(false);
    };

    useImperativeHandle(ref, () => ({ open: onOpen, close: onClose }));

    const handleConfirm = async () => {
      try {
        setIsDeleting(true);
        await onConfirm();
        setOpen(false);
      } catch (error) {
        console.error('Chat delete failed:', error);
      } finally {
        setIsDeleting(false);
      }
    };

    return (
      <Modal
        open={open}
        onClose={onClose}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div className={css.modal} onClick={(e) => e.stopPropagation()}>
          <IconButton
            className={css.closeButton}
            onClick={onClose}
            size="small"
            disabled={isDeleting}
          >
            <CloseIcon />
          </IconButton>
          <h2>Delete chat</h2>
          <div className={css.content}>
            <p>
              Are you sure you want to delete <b>{chatTitle}</b>?
            </p>
            <p className={css.warning}>This action is irreversible!</p>
          </div>
          <div className={classNames(css.actionButtons, isDeleting && css.deleting)}>
            <ActionButton variant="secondary" onClick={onClose} type="button" disabled={isDeleting}>
              Cancel
            </ActionButton>
            <ActionButton variant="danger" onClick={handleConfirm} disabled={isDeleting}>
              {isDeleting ? 'Deleting...' : 'Delete'}
            </ActionButton>
          </div>
        </div>
      </Modal>
    );
  }),
);
